/**
 * 관리 — 사용자 · 시스템 · 내 계정.
 */
import { CircleUser } from 'lucide-react';
import type { ManualChapter } from '../types.js';

export const USERS: ManualChapter = {
  slug: 'users',
  title: '사용자',
  summary: '함께 쓸 사람의 계정을 만들고, 누가 무엇을 할 수 있는지 역할과 담당 범위(사옥·지점)로 정합니다.',
  menuPath: '/users',
  capability: 'user:manage',
  sections: [
    {
      id: 'roles',
      title: '역할 — 누가 무엇을 할 수 있나',
      blocks: [
        {
          kind: 'text',
          text: '계정마다 **역할**을 하나 정합니다. 역할에 따라 사이드바에 보이는 메뉴와 할 수 있는 일이 달라집니다. 처음에는 꼭 필요한 범위만 부여하고, 부족하면 올리는 편이 안전합니다.',
        },
        {
          kind: 'table',
          header: ['역할', '할 수 있는 일', '추가로 보이는 메뉴'],
          rows: [
            ['조회', '콘텐츠·화면·편성을 **보기만** 합니다.', '—'],
            ['감사', '보기에 더해 재생 실적과 “누가 무엇을 바꿨는지” 를 확인합니다. 아무것도 바꿀 수 없습니다.', '리포트'],
            ['콘텐츠 관리', '이미지·영상 같은 콘텐츠를 올리고 고칩니다.', '리포트'],
            ['관리자', '**고객사의 관리 등급**입니다. 콘텐츠에 더해 플레이리스트·스케줄·캠페인을 편성하고, 화면을 등록·관리하고, **긴급 송출**을 냅니다. 자기 팀 계정과 환경 설정(브랜드·운영 시간)도 관리합니다 — 다만 공급사 계정은 목록에 보이지 않고, 그 역할을 줄 수도 없습니다.', '에이전트 설치 · 리포트 · 사용자 · 환경 설정'],
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '메뉴가 보인다고 다 바꿀 수 있는 것은 아닙니다',
          text: '조회·감사 역할에게도 플레이리스트·스케줄·캠페인 메뉴는 보입니다 — 내용을 읽는 데는 권한이 필요 없기 때문입니다. 다만 **바꿀 권한이 없으면 만들기·저장·삭제 단추와 그 기능의 카드가 아예 나오지 않습니다.** 눌렀다가 거절당하는 대신 처음부터 보이지 않으니, 있어야 할 단추가 없으면 역할이 부족하다는 뜻입니다. 관리자에게 요청하세요.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '이 목록에 없는 계정이 하나 더 있습니다',
          text: '서비스를 설치하고 유지보수하는 **공급사 계정**입니다. 이 콘솔의 사용자 목록에는 나오지 않고 역할로 고를 수도 없습니다 — 서버가 감춥니다. 점검·납품 작업이 필요할 때 공급사가 그 계정으로 들어옵니다. 공급사 계정이 한 일은 대시보드의 최근 활동·변경 기록에도 나오지 않습니다.',
        },
      ],
    },
    {
      id: 'scope',
      title: '담당 범위 — 사옥·지점 담당자에게 자기 화면만',
      blocks: [
        {
          kind: 'text',
          text: '사옥이나 지점마다 담당자가 따로 있으면, 그 사람에게 **자기 화면만** 맡길 수 있습니다. 화면을 사옥별 **화면 그룹**으로 묶어 두고, 계정에 맡을 그룹을 고르면 됩니다. 아무 그룹도 고르지 않으면 고객사 전체를 맡습니다(지금까지와 같습니다).',
        },
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '[[화면 그룹]] 메뉴에서 사옥마다 그룹을 만들고, [[화면]] 목록에서 화면을 골라 [[그룹 적용]] 으로 넣어 둡니다.', body: '“화면” 장의 “화면 그룹” 절을 보세요.' },
            { title: '[[사용자]] 메뉴에서 그 사람 줄의 담당 범위 [[바꾸기]] 를 누릅니다.', body: '새로 만들 때는 [[사용자 추가]] 창의 [[담당 범위]] 를 펼쳐 고릅니다(비워 두면 고객사 전체).' },
            { title: '맡길 그룹을 체크하고 [[저장]] 을 누릅니다.', body: '그 사람의 다음 요청부터 바로 적용됩니다. 다시 로그인할 필요가 없습니다.' },
          ],
        },
        {
          kind: 'table',
          header: ['담당 범위가 있는 사람은', ''],
          rows: [
            ['화면', '맡은 그룹의 화면만 보입니다(목록·대시보드·모니터링·리포트). 그 화면만 고치고, 맡은 그룹 밖으로 옮길 수 없습니다. 새로 등록한 화면은 맡은 그룹에 들어갑니다.'],
            ['콘텐츠·플레이리스트·스케줄', '모두 보이고 새로 만들 수 있습니다. 다만 **고치거나 지우는 것은 그것이 나가는 곳이 모두 맡은 범위 안일 때만** 됩니다 — 본사 로비에도 나가는 플레이리스트를 고치면 본사 화면이 바뀌기 때문입니다. 막히면 어디에 나가는지(예: 다른 그룹 1곳 · 캠페인 「가을」) 알려 줍니다 — 맡지 않은 그룹은 이름 대신 개수로만 말합니다. 스케줄이 몇 대에 나가는지도 맡은 화면만 셉니다.'],
            ['캠페인·긴급', '대상으로 **맡은 그룹**만 고를 수 있습니다(전체·태그는 고를 수 없습니다). 다른 곳에 걸린 캠페인은 목록에 “범위 밖” 으로 보이고 고칠 수 없습니다. 긴급 패널의 단추는 [[맡은 화면 비우기]] 가 됩니다. 위쪽 종의 “긴급 오버라이드 작동 중” 은 맡은 화면에 닿는 긴급만 셉니다.'],
            ['메뉴', '사용자 · 환경 설정은 보이지 않습니다 — 고객사 전체에 걸친 일이라서입니다. 화면 오른쪽 위 이름 아래에 “담당 · 강남 사옥” 처럼 맡은 범위가 보입니다.'],
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '담당 범위로 쓰는 그룹은 지울 수 없습니다',
          text: '그룹을 지우면 그 그룹만 맡던 계정이 범위를 잃어 **고객사 전체**를 맡게 되기 때문입니다. 계정의 담당 범위를 먼저 바꾼 뒤 지우세요. 자기 계정의 범위는 스스로 좁힐 수 없습니다 — 그 순간 계정 관리를 잃습니다.',
        },
      ],
    },
    {
      id: 'sso',
      title: '사내 계정으로 들어오는 사람',
      blocks: [
        {
          kind: 'text',
          text: '서버에 사내 계정 로그인(Entra ID·Okta 등)이 켜져 있으면, 처음 들어온 사람의 계정이 저절로 만들어집니다. 목록에서 이름 옆에 “사내 계정” 표시가 붙습니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '역할은 사내 계정 시스템이 정합니다',
          text: '사내 계정은 로그인할 때마다 사내 계정 시스템에 정해 둔 역할로 맞춰집니다. 여기서 역할을 바꿔도 다음 로그인 때 돌아가니, 역할은 사내 계정 시스템의 담당 부서에 요청하세요. **담당 범위는 여기서 정한 것이 그대로 남습니다.** 사내 계정에는 비밀번호가 없어 [[…]] 에 [[비밀번호 재설정]] 도 없습니다. 퇴사자는 사내 계정 시스템에서 막으면 다음 로그인부터 들어오지 못하고, 당장 끊으려면 여기서 [[비활성화]] 합니다.',
        },
      ],
    },
    {
      id: 'add',
      title: '사용자 추가하기',
      blocks: [
        {
          kind: 'text',
          text: '초대 메일을 보내는 방식이 아닙니다. 콘솔이 **임시 비밀번호**를 만들어 주면, 관리자가 그것을 당사자에게 직접 전합니다.',
        },
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '[[사용자]] 메뉴에서 오른쪽 위 [[사용자 추가]] 를 누릅니다.' },
            { title: '이름·이메일을 넣고 역할을 고릅니다.', body: '역할을 고르면 그 역할이 할 수 있는 일이 바로 아래에 나옵니다. 기본값은 ‘관리자’ 입니다.' },
            { title: '[[만들기]] 를 누릅니다.' },
            {
              title: '나타난 임시 비밀번호를 [[복사]] 해서 당사자에게 전합니다.',
              body: '메신저·전화 등 신뢰할 수 있는 방법으로 전달하세요. 다 전했으면 [[전달 완료]] 로 창을 닫습니다.',
            },
            { title: '받은 사람이 처음 로그인하면 비밀번호를 새로 정하는 화면이 먼저 뜹니다.', body: '새 비밀번호를 정해야 콘솔을 쓸 수 있습니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '임시 비밀번호는 한 번만 보입니다',
          text: '창을 닫으면 다시 볼 수 없고 서버에도 남지 않습니다. 전하기 전에 창을 닫았다면 그 줄의 [[…]] ▸ [[비밀번호 재설정]] 으로 새로 만드세요.',
        },
      ],
    },
    {
      id: 'manage',
      title: '계정 관리하기',
      blocks: [
        {
          kind: 'actions',
          title: '각 줄에서 할 수 있는 일 — 역할 말고는 줄 끝의 [[…]] 안에',
          items: [
            { label: '역할', text: '줄마다 있는 목록에서 고르면 **즉시 저장**됩니다(확인 창 없음). 역할이 바뀐 사람은 로그인이 끊겨 다시 들어와야 합니다.' },
            { label: '비밀번호 재설정', text: '새 임시 비밀번호를 만듭니다. 잠긴 계정도 이것으로 풀립니다. 그 사람의 모든 로그인이 끊깁니다.' },
            { label: '비활성화', text: '계정을 지우지 않고 로그인만 막습니다. 퇴사·휴직처럼 나중에 다시 쓸 수 있을 때 씁니다. [[활성화]] 로 되돌립니다.' },
            { label: '삭제', text: '계정을 없앱니다. **되돌릴 수 없습니다.** 활동 기록은 남습니다.' },
          ],
        },
        {
          kind: 'legend',
          title: '상태 표시',
          items: [
            { label: '사용 중', tone: 'ok', meaning: '정상적으로 사용 중인 계정입니다.' },
            { label: '임시 비밀번호', tone: 'warn', meaning: '아직 첫 로그인을 하지 않았거나 비밀번호를 바꾸지 않았습니다.' },
            { label: '잠김', tone: 'danger', meaning: '비밀번호를 여러 번(기본 10회) 틀려 15분 동안 잠겼습니다. 기다리거나 [[…]] ▸ [[비밀번호 재설정]] 을 누르세요.' },
            { label: '비활성', tone: 'neutral', meaning: '관리자가 로그인을 막아 둔 계정입니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '자기 자신의 계정은 삭제할 수 없습니다',
          text: '담당자가 바뀔 때는 **새 관리자 계정을 먼저 만들어 넘겨준 뒤** 기존 계정을 정리하세요. 관리자가 한 명도 남지 않으면 팀 계정을 관리할 사람이 없어집니다.',
        },
      ],
    },
  ],
};
export const ACCOUNT: ManualChapter = {
  slug: 'account',
  title: '내 계정',
  summary: '비밀번호를 바꾸고, 내 계정으로 로그인된 다른 기기를 정리합니다.',
  menuPath: '/account',
  icon: CircleUser,
  sections: [
    {
      id: 'open',
      title: '여는 방법',
      blocks: [
        {
          kind: 'text',
          text: '사이드바가 아니라 **오른쪽 위의 동그란 이름 표시**를 누르면 나오는 메뉴에서 [[내 계정]] 을 고릅니다. 같은 메뉴에 [[로그아웃]] 도 있습니다.',
        },
      ],
    },
    {
      id: 'password',
      title: '비밀번호 변경',
      blocks: [
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '현재 비밀번호를 넣습니다.' },
            { title: '새 비밀번호를 두 번 넣습니다.', body: '8자 이상이면 됩니다. 짧고 복잡한 글자보다 **긴 문장**이 더 안전합니다.' },
            { title: '[[비밀번호 변경]] 을 누릅니다.', body: '지금 쓰는 브라우저는 그대로 로그인되어 있고, **다른 기기의 로그인은 모두 해제**됩니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '사내 계정으로 들어온 경우',
          text: '로그인 화면의 사내 계정 단추로 들어왔거나 다른 사내 시스템을 통해 콘솔을 열었다면, 비밀번호와 2단계 인증은 사내 계정 시스템에서 관리합니다. 이 화면에는 비밀번호 변경 칸이 나오지 않고, “로그인 방식” 에 “사내 계정” 이 적힙니다. 사내 계정으로 연 로그인은 하루에 한 번쯤(12시간) 다시 확인합니다.',
        },
      ],
    },
    {
      id: 'devices',
      title: '로그인된 기기 정리하기',
      blocks: [
        {
          kind: 'text',
          text: '내 계정으로 로그인되어 있는 브라우저 목록이 “Chrome · Windows” 처럼 보입니다. 지금 보고 있는 브라우저에는 “이 기기” 표시가 붙습니다.',
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '공용 PC에서 로그아웃을 잊었다면',
          text: '[[다른 기기 모두 로그아웃]] 을 누르세요. 지금 이 브라우저만 남기고 나머지 로그인이 한꺼번에 끊깁니다. 비밀번호가 유출되었을 가능성이 있다면 비밀번호도 함께 바꾸세요.',
        },
      ],
    },
  ],
};
